import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Laptop, Smartphone, Headphones, Monitor, IdCard, Keyboard, HardDrive, Plus, Edit, RotateCcw, UserPlus, Trash2, Package, CheckCircle2, Archive, Box } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { PageHeader, KPIGrid, KPICard, EmptyState } from "@/components/app/PageKit";

const ICONS = { laptop: Laptop, mobile: Smartphone, headset: Headphones, monitor: Monitor, id_card: IdCard, keyboard: Keyboard };

export default function Assets() {
  const { user } = useAuth();
  const loc = useLocation();
  const canManage = ["super_admin", "hr_admin", "admin", "it_support", "admin_ops"].includes(user?.role) || user?.is_platform_owner;
  const isEmployee = !canManage;
  const [assets, setAssets] = useState([]);
  const [emps, setEmps] = useState([]);
  const [tab, setTab] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [form, setForm] = useState({ asset_name: "", asset_type: "laptop", brand: "", model: "", serial_number: "", cost: 0, vendor: "", location: "HQ", status: "in_stock" });
  const [assignee, setAssignee] = useState("");

  const load = async () => {
    const [a, e] = await Promise.all([api.get("/assets"), api.get("/employees")]);
    setAssets(a.data); setEmps(e.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (loc.state?.openAdd && canManage) setAddOpen(true); }, [loc.state, canManage]);

  const create = async () => {
    try { await api.post("/assets", { ...form, cost: Number(form.cost) || 0 }); toast.success("Asset created"); setAddOpen(false); setForm({ asset_name: "", asset_type: "laptop", brand: "", model: "", serial_number: "", cost: 0, vendor: "", location: "HQ", status: "in_stock" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const update = async () => {
    try {
      await api.patch(`/assets/${editAsset.id}`, {
        asset_name: editAsset.asset_name, brand: editAsset.brand, model: editAsset.model,
        serial_number: editAsset.serial_number, location: editAsset.location, condition: editAsset.condition,
        status: editAsset.status, vendor: editAsset.vendor,
      });
      toast.success("Updated"); setEditAsset(null); load();
    } catch { toast.error("Failed"); }
  };

  const assign = async () => {
    try { await api.post("/assets/assign", { asset_id: assignFor.id, employee_id: assignee }); toast.success("Asset assigned"); setAssignFor(null); setAssignee(""); load(); }
    catch { toast.error("Failed"); }
  };

  const returnAsset = async (id) => {
    try { await api.post(`/assets/${id}/return`); toast.success("Returned"); load(); } catch { toast.error("Failed"); }
  };

  const retire = async (id) => {
    if (!window.confirm("Retire this asset?")) return;
    try { await api.delete(`/assets/${id}`); toast.success("Asset retired"); load(); } catch { toast.error("Failed"); }
  };

  const filtered = tab === "all" ? assets : assets.filter(a => a.status === tab);
  const stats = {
    total: assets.length,
    assigned: assets.filter(a => a.status === "assigned").length,
    in_stock: assets.filter(a => a.status === "in_stock").length,
    retired: assets.filter(a => a.status === "retired").length,
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-full overflow-x-hidden" data-testid="assets-page">
      <PageHeader
        category="Lifecycle"
        title={isEmployee ? "My Assets" : "Assets"}
        description={isEmployee ? "Hardware & equipment assigned to you by the company" : "Hardware, peripherals & company-issued equipment lifecycle"}
        primaryAction={canManage ? { label: "Add Asset", icon: Plus, onClick: () => setAddOpen(true), testid: "add-asset-btn" } : undefined}
      />

      <KPIGrid cols={4}>
        <KPICard icon={Package}      tone="violet"  label="Total"     value={stats.total} testid="kpi-total" />
        <KPICard icon={CheckCircle2} tone="emerald" label="Assigned"  value={stats.assigned} testid="kpi-assigned" />
        <KPICard icon={Box}          tone="cyan"    label="In Stock"  value={stats.in_stock} testid="kpi-stock" />
        <KPICard icon={Archive}      tone="rose"    label="Retired"   value={stats.retired} testid="kpi-retired" />
      </KPIGrid>

      {canManage && (
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>New Asset</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={form.asset_name} onChange={(ev) => setForm({...form, asset_name: ev.target.value})} className="mt-1" data-testid="asset-name"/></div>
                  <div><Label>Type</Label>
                    <Select value={form.asset_type} onValueChange={(v) => setForm({...form, asset_type: v})}>
                      <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="laptop">Laptop</SelectItem>
                        <SelectItem value="mobile">Mobile</SelectItem>
                        <SelectItem value="headset">Headset</SelectItem>
                        <SelectItem value="monitor">Monitor</SelectItem>
                        <SelectItem value="keyboard">Keyboard</SelectItem>
                        <SelectItem value="id_card">ID Card</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Brand</Label><Input value={form.brand} onChange={(ev) => setForm({...form, brand: ev.target.value})} className="mt-1"/></div>
                  <div><Label>Model</Label><Input value={form.model} onChange={(ev) => setForm({...form, model: ev.target.value})} className="mt-1"/></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Serial Number</Label><Input value={form.serial_number} onChange={(ev) => setForm({...form, serial_number: ev.target.value})} className="mt-1"/></div>
                  <div><Label>Cost</Label><Input type="number" value={form.cost} onChange={(ev) => setForm({...form, cost: ev.target.value})} className="mt-1"/></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Vendor</Label><Input value={form.vendor} onChange={(ev) => setForm({...form, vendor: ev.target.value})} className="mt-1"/></div>
                  <div><Label>Location</Label><Input value={form.location} onChange={(ev) => setForm({...form, location: ev.target.value})} className="mt-1"/></div>
                </div>
              </div>
              <DialogFooter><Button onClick={create} disabled={!form.asset_name || !form.serial_number} className="qh-grad-bg text-white" data-testid="asset-submit">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        {!isEmployee && (
          <TabsList className="bg-slate-100">
            <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="in_stock">In Stock ({stats.in_stock})</TabsTrigger>
            <TabsTrigger value="assigned">Assigned ({stats.assigned})</TabsTrigger>
            <TabsTrigger value="retired">Retired ({stats.retired})</TabsTrigger>
          </TabsList>
        )}
        <TabsContent value={tab} className="mt-4">
          {filtered.length === 0 ? (
            <EmptyState
              icon={Package}
              title={tab === "all" ? "No assets yet" : `No ${tab.replace("_", " ")} assets`}
              message={canManage ? "Add laptops, monitors and accessories to track assignment, returns and lifecycle." : "When IT assigns hardware to you, it'll appear here."}
              action={canManage && tab === "all" ? { label: "Add Asset", icon: Plus, onClick: () => setAddOpen(true) } : undefined}
            />
          ) : (
          <Card className="border-slate-200 shadow-sm overflow-hidden bg-white">
            <div className="max-h-[65vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-[0.1em] text-slate-500">
                    <th className="px-4 py-3">Asset</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Brand</th>
                    <th className="px-4 py-3">Serial</th><th className="px-4 py-3">Assigned To</th><th className="px-4 py-3">Status</th>
                    {canManage && <th className="px-4 py-3">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => {
                    const Icon = ICONS[a.asset_type] || HardDrive;
                    return (
                      <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`asset-row-${a.id}`}>
                        <td className="px-4 py-3"><div className="flex items-center gap-2"><Icon className="w-4 h-4 text-slate-500"/><span className="font-medium text-slate-900">{a.asset_name}</span></div></td>
                        <td className="px-4 py-3 capitalize text-slate-700">{a.asset_type?.replace("_"," ")}</td>
                        <td className="px-4 py-3 text-slate-700">{a.brand}</td>
                        <td className="px-4 py-3 text-slate-600 font-mono text-xs">{a.serial_number}</td>
                        <td className="px-4 py-3 text-slate-700">{a.assignee?.full_name || "—"}</td>
                        <td className="px-4 py-3">
                          <Badge className={`border-0 capitalize ${a.status === "assigned" ? "bg-emerald-100 text-emerald-700" : a.status === "in_stock" ? "bg-cyan-100 text-cyan-700" : "bg-slate-200 text-slate-600"}`}>{a.status?.replace("_"," ")}</Badge>
                        </td>
                        {canManage && (
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              {a.status === "in_stock" && <Button size="sm" variant="outline" onClick={() => setAssignFor(a)} className="h-7" data-testid={`asset-assign-${a.id}`}><UserPlus className="w-3.5 h-3.5"/></Button>}
                              {a.status === "assigned" && <Button size="sm" variant="outline" onClick={() => returnAsset(a.id)} className="h-7" data-testid={`asset-return-${a.id}`}><RotateCcw className="w-3.5 h-3.5"/></Button>}
                              <Button size="sm" variant="outline" onClick={() => setEditAsset({...a})} className="h-7" data-testid={`asset-edit-${a.id}`}><Edit className="w-3.5 h-3.5"/></Button>
                              <Button size="sm" variant="ghost" onClick={() => retire(a.id)} className="h-7 text-red-600" data-testid={`asset-retire-${a.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Assign dialog */}
      <Dialog open={!!assignFor} onOpenChange={(v) => !v && setAssignFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign {assignFor?.asset_name}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label>Employee</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select employee"/></SelectTrigger>
              <SelectContent>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button onClick={assign} disabled={!assignee} className="bg-slate-900 hover:bg-slate-800">Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editAsset} onOpenChange={(v) => !v && setEditAsset(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Asset</DialogTitle></DialogHeader>
          {editAsset && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={editAsset.asset_name} onChange={(ev) => setEditAsset({...editAsset, asset_name: ev.target.value})} className="mt-1"/></div>
                <div><Label>Serial</Label><Input value={editAsset.serial_number} onChange={(ev) => setEditAsset({...editAsset, serial_number: ev.target.value})} className="mt-1"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Brand</Label><Input value={editAsset.brand} onChange={(ev) => setEditAsset({...editAsset, brand: ev.target.value})} className="mt-1"/></div>
                <div><Label>Model</Label><Input value={editAsset.model} onChange={(ev) => setEditAsset({...editAsset, model: ev.target.value})} className="mt-1"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Location</Label><Input value={editAsset.location || ""} onChange={(ev) => setEditAsset({...editAsset, location: ev.target.value})} className="mt-1"/></div>
                <div><Label>Condition</Label>
                  <Select value={editAsset.condition || "good"} onValueChange={(v) => setEditAsset({...editAsset, condition: v})}>
                    <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="damaged">Damaged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={update} className="bg-slate-900 hover:bg-slate-800">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
